/**
 * @fileoverview Sistema de logging estructurado para el módulo API Munzen
 * Proporciona logging con niveles, contexto y request ID para debugging
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

/**
 * Niveles de logging disponibles
 */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/**
 * Estructura de un log entry
 */
export interface LogEntry {
  level: LogLevel;
  timestamp: number;
  requestId?: string;
  method?: string;
  url?: string;
  status?: number;
  message: string;
  data?: unknown;
  error?: unknown;
}

/**
 * Configuración del logger
 */
interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
}

/**
 * Niveles de logging ordenados por severidad
 */
const LOG_LEVELS: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

/**
 * Configuración por defecto del logger
 * Solo activo en desarrollo
 */
const config: LoggerConfig = {
  enabled: import.meta.env.DEV,
  minLevel: 'debug',
};

/**
 * Genera un ID único para una petición
 * @returns UUID v4
 *
 * @example
 * const requestId = generateRequestId();
 * // '550e8400-e29b-41d4-a716-446655440000'
 */
export const generateRequestId = (): string => {
  return uuidv4();
};

/**
 * Verifica si un nivel de log debe ser registrado
 * @param level - Nivel del log a verificar
 * @returns true si el log debe ser registrado
 */
const shouldLog = (level: LogLevel): boolean => {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] <= LOG_LEVELS[config.minLevel];
};

/**
 * Formatea un log entry para la consola
 * @param entry - Entry a formatear
 * @returns String formateado para consola
 */
const formatLogEntry = (entry: LogEntry): string => {
  // Usar moment para mostrar hora local en formato legible
  // Crear moment object desde timestamp en milisegundos
  const timestamp = moment(entry.timestamp, 'x').format('YYYY-MM-DD HH:mm:ss.SSS');
  const parts = [
    `[${timestamp}]`,
    `[${entry.level.toUpperCase()}]`,
  ];

  if (entry.requestId) {
    parts.push(`[${entry.requestId.substring(0, 8)}]`);
  }

  if (entry.method && entry.url) {
    parts.push(`${entry.method} ${entry.url}`);
  }

  if (entry.status) {
    parts.push(`(${entry.status})`);
  }

  parts.push(entry.message);

  return parts.join(' ');
};

/**
 * Registra un log entry en la consola
 * @param entry - Entry a registrar
 */
const writeLog = (entry: LogEntry): void => {
  if (!shouldLog(entry.level)) return;

  const formatted = formatLogEntry(entry);

  // Usar método de consola apropiado según nivel
  switch (entry.level) {
    case 'error':
      console.error(formatted, entry.data, entry.error);
      break;
    case 'warn':
      console.warn(formatted, entry.data);
      break;
    case 'info':
      console.info(formatted, entry.data);
      break;
    case 'debug':
      console.debug(formatted, entry.data);
      break;
  }
};

/**
 * Logger para errores
 * @param message - Mensaje del error
 * @param context - Contexto adicional
 *
 * @example
 * logError('Error en petición', {
 *   requestId: 'abc-123',
 *   method: 'GET',
 *   url: '/api/clientes',
 *   status: 500,
 *   error: error
 * });
 */
export const logError = (
  message: string,
  context?: Partial<Omit<LogEntry, 'level' | 'timestamp' | 'message'>>,
): void => {
  writeLog({
    level: 'error',
    timestamp: Date.now(),
    message,
    ...context,
  });
};

/**
 * Logger para warnings
 * @param message - Mensaje del warning
 * @param context - Contexto adicional
 *
 * @example
 * logWarn('Token próximo a expirar', {
 *   requestId: 'abc-123',
 *   data: { expiresIn: 30000 }
 * });
 */
export const logWarn = (
  message: string,
  context?: Partial<Omit<LogEntry, 'level' | 'timestamp' | 'message'>>,
): void => {
  writeLog({
    level: 'warn',
    timestamp: Date.now(),
    message,
    ...context,
  });
};

/**
 * Logger para información
 * @param message - Mensaje informativo
 * @param context - Contexto adicional
 *
 * @example
 * logInfo('Token refrescado exitosamente', {
 *   requestId: 'abc-123',
 *   data: { newExpiration: timestamp }
 * });
 */
export const logInfo = (
  message: string,
  context?: Partial<Omit<LogEntry, 'level' | 'timestamp' | 'message'>>,
): void => {
  writeLog({
    level: 'info',
    timestamp: Date.now(),
    message,
    ...context,
  });
};

/**
 * Logger para debugging
 * @param message - Mensaje de debug
 * @param context - Contexto adicional
 *
 * @example
 * logDebug('Verificando expiración de token', {
 *   requestId: 'abc-123',
 *   data: { timeUntilExpiration: 60000 }
 * });
 */
export const logDebug = (
  message: string,
  context?: Partial<Omit<LogEntry, 'level' | 'timestamp' | 'message'>>,
): void => {
  writeLog({
    level: 'debug',
    timestamp: Date.now(),
    message,
    ...context,
  });
};

/**
 * Configura el logger
 * @param newConfig - Nueva configuración
 *
 * @example
 * // Deshabilitar logging
 * configureLogger({ enabled: false });
 *
 * // Cambiar nivel mínimo
 * configureLogger({ minLevel: 'warn' });
 */
export const configureLogger = (newConfig: Partial<LoggerConfig>): void => {
  Object.assign(config, newConfig);
};
